import { AuditActions, DebtorContactDTO } from '@/models/back-office-pages/clients'
import { InvoicesBillingAddressesDTO } from '@/models/back-office-pages/debt-collection'
import { InvoiceDebtorVerificationDetailsDTO } from '@/models/back-office-pages/invoice-verification'
import {
  BOInvoiceStatus,
  DownloadableFileDTO,
  InvoiceDTO,
  InvoiceVerificationTemplate,
  InvoiceVerifierInput,
} from '@/models/back-office-pages/invoices'
import { AdministrationTypes, Currency, FromToFilterInput, MinMaxFilterInput } from '@/models/common'
import { Direction } from '@/models/table'

export enum OutstandingVerificationStatus {
  APPROVED = 'APPROVED',
  ARCHIVED = 'ARCHIVED',
  DEBTOR_RESPONDED = 'DEBTOR_RESPONDED',
  NEW = 'NEW',
  REJECTED = 'REJECTED',
  SENT = 'SENT',
  UNDER_INVESTIGATION = 'UNDER_INVESTIGATION',
}

export interface InvoiceOutstandingDataDTO {
  dueDate: Date
  factorAmount: number
  fundedAmount: number
  invoiceAmount: number
  invoiceId: string
  invoiceNumber: string
  issueDate: Date
  outstandingAmount: number
  status: BOInvoiceStatus
  currency: Currency
  selected?: boolean

  [key: string]: unknown
}

export interface InvoiceOutstandingVerificationDTO {
  clientDebtorId: string
  clientId: string
  clientName: string
  country: string
  debtorName: string
  id: string
  lastVerificationSentAt: Date
  sourcingAgentAdministrates: string
  sourcingAgentName: string
  startDate: Date
  status: OutstandingVerificationStatus
  totalOutstanding: number
  verificationConfirmedAt: Date
  invoices: Array<InvoiceOutstandingDataDTO>
}

export interface InvoiceOutstandingVerificationTotalsDTO {
  totalOutstanding: number
}

export interface PageWithTotals_InvoiceOutstandingVerificationDTO_InvoiceOutstandingVerificationTotalsDTO {
  content: Array<InvoiceOutstandingVerificationDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
  totals: InvoiceOutstandingVerificationTotalsDTO
}

export interface OutstandingVerificationTasksData {
  filterInvoiceOutstandingVerifications: PageWithTotals_InvoiceOutstandingVerificationDTO_InvoiceOutstandingVerificationTotalsDTO
}

export enum OutstandingVerificationFilterStatus {
  ALL = 'ALL',
  APPROVED = 'APPROVED',
  ARCHIVED = 'ARCHIVED',
  DEBTOR_RESPONDED = 'DEBTOR_RESPONDED',
  NEW = 'NEW',
  REJECTED = 'REJECTED',
  SENT = 'SENT',
  UNDER_INVESTIGATION = 'UNDER_INVESTIGATION',
}

export enum InvoiceVerificationResponse {
  NEW = 'NEW',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export interface OutstandingVerificationTasksVariables {
  totalOutstanding?: MinMaxFilterInput
  clientName?: string
  sourcingAgentAdministrates?: AdministrationTypes[]
  debtorName?: string
  verificationConfirmedAt?: FromToFilterInput
  startDate?: FromToFilterInput
  status?: [OutstandingVerificationStatus]
  tableStatus: OutstandingVerificationFilterStatus
  sortDirection: Direction
  sortField: string
  pageSize: number
  pageNum: number
}

export interface CreateNewOperationTaskData {
  createInvoiceOutstandingVerifications: InvoiceOutstandingVerificationDTO
}

export interface CreateNewOperationTaskVariables {
  clientDebtorId: string
  startDate: string
}

export interface CreateNewOperationTaskFormValues {
  clientId: string
  clientDebtorId: string
  startDate: string
}

export enum CreateNewOperationTaskFieldsNames {
  CLIENT_ID = 'clientId',
  CLIENT_DEBTOR_ID = 'clientDebtorId',
  FIRST_VERIFICATION_DATE = 'startDate',
}

export interface InvoiceVerificationDecisionDTO {
  comment: string
  debtorsEmailDocumentId: string
  debtorsEmailDocumentName: string
  decision: InvoiceVerificationResponse
  decisionAt: Date
  decisionBy: string
  sourcingAgentUserName: string
  sourcingAgentEnteredDecisionAt: Date
}

export interface InvoiceOutstandingVerifierDTO {
  email: string
  sentAt: Date
  id: string
}

export interface InvoiceOutstandingVerificationLogDTO {
  createdAt: Date
  debtorComments: string
  debtorResponse: InvoiceVerificationResponse
  event: AuditActions
  id: string
  comments: string
  sentTo: Array<string>
  userFullName: string
}

export interface InvoiceOutstandingVerificationDetailsDTO {
  id: string
  sourcingAgentAdministrates: string
  sourcingAgentName: string
  clientId: string
  clientName: string
  country: string
  debtorId: string
  clientDebtorId: string
  debtorName: string
  billingAddress: DebtorContactDTO
  invoices: Array<InvoiceOutstandingDataDTO>
  selectedInvoices: Array<InvoiceOutstandingDataDTO>
  debtorDecision: InvoiceVerificationDecisionDTO
  otherDecisions: Array<InvoiceVerificationDecisionDTO>
  startDate: Date
  status: OutstandingVerificationStatus
  totalOutstanding: number
  totalOutstandingPLN: number
  verificationComments: string
  verificationConfirmedAt: Date
  archivedAt: Date
  investigationAt: Date
  verificationConfirmedBy: string
  verifiers: Array<InvoiceOutstandingVerifierDTO>
  logs: Array<InvoiceOutstandingVerificationLogDTO>
}

export interface OutstandingVerificationTaskDetailsData {
  loadInvoiceOutstandingVerifications: InvoiceOutstandingVerificationDetailsDTO
}

export interface OutstandingVerificationTaskDetailsVariables {
  id: string
}

export interface ArchiveOutstandingVerificationTaskData {
  archiveInvoiceOutstandingVerification: InvoiceOutstandingVerificationDetailsDTO
}

export interface ArchiveOutstandingVerificationTaskVariables {
  id: string
  comments: string
}

export interface ArchiveOutstandingVerificationTaskFormValues {
  comments: string
}

export enum OutstandingVerificationTaskFieldValues {
  COMMENT = 'comments',
}

export interface StartOutstandingAmountVerificationData {
  sendInvoiceOutstandingVerification: InvoiceOutstandingVerificationDetailsDTO
}

export interface StartOutstandingAmountVerificationVariables {
  sendTo: Array<string>
  billingAddressId: string
  id: string
  invoiceIds: Array<string>
}

export interface StartOutstandingAmountVerificationFormValues {
  sendTo: Array<string>
  billingAddressId: string
  selectedInvoices: Array<InvoiceOutstandingDataDTO>
}
export interface OutstandingAmountVerificationFromEmailVariables {
  debtorResponseDate: string
  debtorEmailFile: DownloadableFileDTO
  comment: string
  verificationId: string
}

export interface InvoiceDebtorOutstandingVerificationDetailsDTO {
  amount: number
  clientCompanyName: string
  comment: string
  debtorCompanyName: string
  decisionMadeAt: Date
  iban: string
  sourcingAgentCountry: string
  startDate: Date
  status: InvoiceVerificationResponse
}

export interface ApproveOutstandingAmountVerificationFromEmailData {
  approveInvoiceOutstandingVerificationFromDebtorEmail: InvoiceDebtorOutstandingVerificationDetailsDTO
}

export interface RejectOutstandingAmountVerificationFromEmailData {
  rejectInvoiceOutstandingVerificationFromDebtorEmail: InvoiceDebtorOutstandingVerificationDetailsDTO
}

export interface DebtorResponseFormData extends OutstandingAmountVerificationFromEmailVariables {
  debtorResponse: boolean
}

export enum DebtorResponseFieldsNames {
  VERIFICATION_ID = 'verificationId',
  DATE = 'debtorResponseDate',
  RESPONSE = 'debtorResponse',
  FILE = 'debtorEmailFile',
  COMMENT = 'comment',
}

export enum StartOutstandingAmountVerificationFieldValues {
  SEND_TO = 'sendTo',
  SELECTED_INVOICES = 'selectedInvoices',
  INVOICES = 'invoices',
  BILLING_ADDRESS_ID = 'billingAddressId',
}

export enum InvoicesForVerificationFieldArrayValues {
  SELECTED = 'selected',
  OUTSTANDING_AMOUNT = 'outstandingAmount',
}

export enum InvoicesForVerificationTableAssessor {
  INVOICE_NUMBER = 'invoiceNumber',
  INVOICE_AMOUNT = 'invoiceAmount',
  FACTOR_AMOUNT = 'factorAmount',
  FUNDED_AMOUNT = 'fundedAmount',
  OUTSTANDING_AMOUNT = 'outstandingAmount',
  ISSUE_DATE = 'issueDate',
  DUE_DATE = 'dueDate',
  STATUS = 'status',
}

export enum OutstandingAmountVerificationFormType {
  APPROVE = 'APPROVE',
  REJECT = 'REJECT',
  UNDER_INVESTIGATION = 'UNDER_INVESTIGATION',
  EXTRA_VERIFICATION = 'EXTRA_VERIFICATION',
  SEND_EMAILS = 'SEND_EMAILS',
  DEBTOR_RESPONSE = 'DEBTOR_RESPONSE',
}

export interface ApproveOutstandingAmountVerificationData {
  approveInvoiceOutstandingVerification: InvoiceOutstandingVerificationDetailsDTO
}

export interface ApproveOutstandingAmountVerificationVariables extends OutstandingAmountVerificationFormData {
  id: string
}

export interface RejectOutstandingAmountVerificationData {
  rejectInvoiceOutstandingVerification: InvoiceDebtorVerificationDetailsDTO
}

export interface RejectOutstandingAmountVerificationVariables extends OutstandingAmountVerificationFormData {
  id: string
}

export interface StartOutstandingAmountVerificationInvestigationData {
  startInvoiceOutstandingVerificationInvestigation: InvoiceOutstandingVerificationDetailsDTO
}

export interface StartOutstandingAmountVerificationInvestigationVariables extends OutstandingAmountVerificationFormData {
  id: string
}

export interface OutstandingAmountVerificationFormData {
  comments: string
  formType?: OutstandingAmountVerificationFormType
}

export enum OutstandingAmountVerificationFieldsNames {
  COMMENT = 'comments',
  FORM_TYPE = 'formType',
}

export interface InvoiceOutstandingVerificationCommentDTO {
  archived: boolean
  comment: string
  createdBy: string
  createdByEmail: string
  createdById: string
  createdOn: Date
  flagged: boolean
  id: string
  invoiceOutstandingVerificationId: string
  updatedBy: string
  updatedByEmail: string
  updatedById: string
  updatedOn: Date
}

export interface GetOperationTaskCommentsData {
  getInvoiceOutstandingVerificationComments: Array<InvoiceOutstandingVerificationCommentDTO>
}

export interface GetOperationTaskCommentsVariables {
  id: string
}

export interface GetOperationTaskCommentByIdData {
  getInvoiceOutstandingVerificationCommentById: InvoiceOutstandingVerificationCommentDTO
}

export interface GetOperationTaskCommentByIdVariables {
  commentId: string
}

export interface AddOperationTaskCommentData {
  addInvoiceOutstandingVerificationComment: InvoiceOutstandingVerificationCommentDTO
}

export interface AddOperationTaskCommentVariables {
  id: string
  comment: string
}

export interface UpdateOperationTaskCommentData {
  updateInvoiceOutstandingVerificationComment: InvoiceOutstandingVerificationCommentDTO
}

export interface UpdateOperationTaskCommentVariables {
  comment: string
  commentId: string
}

export interface MarkOperationTaskCommentAsFlaggedData {
  markInvoiceOutstandingVerificationCommentAsFlagged: InvoiceOutstandingVerificationCommentDTO
}

export interface MarkOperationTaskCommentAsFlaggedVariables {
  commentId: string
}

export interface MarkOperationTaskCommentAsUnflaggedData {
  markInvoiceOutstandingVerificationCommentAsUnflagged: InvoiceOutstandingVerificationCommentDTO
}

export interface MarkOperationTaskCommentAsUnflaggedVariables {
  commentId: string
}

export interface MarkOperationTaskCommentAsArchivedData {
  markInvoiceOutstandingVerificationCommentAsArchived: InvoiceOutstandingVerificationCommentDTO
}

export interface MarkOperationTaskCommentAsArchivedVariables {
  commentId: string
}

export interface MarkOperationTaskCommentAsUnarchivedData {
  markInvoiceOutstandingVerificationCommentAsUnarchived: InvoiceOutstandingVerificationCommentDTO
}

export interface MarkOperationTaskCommentAsUnarchivedVariables {
  commentId: string
}

export interface InvoiceBulkVerificationDTO {
  clientId: string
  clientName: string
  createdBy: string
  createdOn: Date
  debtorId: string
  debtorName: string
  id: string
  lastVerificationSent: Date
  sourcingAgentAdministrates: AdministrationTypes
  sourcingAgentId: string
  status: InvoiceBulkVerificationStatus
  totalInvoices: number
  verificationCompletedAt: Date
  verifiedInvoices: number
}

export interface Page_InvoiceBulkVerificationDTO {
  content: Array<InvoiceBulkVerificationDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface BulkExternalVerificationTasksData {
  filterInvoiceBulkVerifications: Page_InvoiceBulkVerificationDTO
}

export interface BulkExternalVerificationTasksVariables {
  clientName?: string
  debtorName?: string
  sourcingAgentAdministrates?: Array<AdministrationTypes>
  lastVerificationSent?: FromToFilterInput
  sortDirection?: Direction
  tableFilterStatus: InvoiceBulkVerificationFilterStatus
  createdBy?: string
  sortField?: string
  verificationCompletedAt?: FromToFilterInput
  pageSize?: number
  pageNum?: number
  status?: Array<InvoiceBulkVerificationStatus>
}

export enum InvoiceBulkVerificationStatus {
  ARCHIVED = 'ARCHIVED',
  IN_VERIFICATION = 'IN_VERIFICATION',
  VERIFIED = 'VERIFIED',
}

export enum InvoiceBulkVerificationFilterStatus {
  ALL = 'ALL',
  ARCHIVED = 'ARCHIVED',
  IN_VERIFICATION = 'IN_VERIFICATION',
  VERIFIED = 'VERIFIED',
}

export enum BulkVerificationAuditActions {
  INVOICE_BULK_VERIFICATION_CREATED = 'INVOICE_BULK_VERIFICATION_CREATED',
  INVOICE_BULK_VERIFICATION_RESENT = 'INVOICE_BULK_VERIFICATION_RESENT',
  INVOICE_BULK_VERIFICATION_ARCHIVED = 'INVOICE_BULK_VERIFICATION_ARCHIVED',
}

export interface BulkInvoiceApproval {
  decision: ApproveRejectChoice
  invoiceId: string
  invoiceNumber: string
}

export interface InvoiceBulkVerificationAuditDTO {
  action: BulkVerificationAuditActions
  comments: string
  createdAt: Date
  emails: string[]
  fileId: string
  fileName: string
  id: string
  userFullName: string
  invoices: Array<BulkInvoiceApproval>
}

export interface InvoiceBulkVerificationDetailsDTO {
  bulkVerificationId: string
  status: InvoiceBulkVerificationStatus
  clientDebtorId: string
  clientId: string
  clientName: string
  debtorName: string
  debtorPhone: string
  verifierEmails: string[]
  invoices: Array<InvoiceDTO>
  invoicesBillingAddresses: Array<InvoicesBillingAddressesDTO>
  logs: Array<InvoiceBulkVerificationAuditDTO>
}

export interface InvoiceBulkVerificationTaskDetailsData {
  loadInvoiceBulkVerificationDetails: InvoiceBulkVerificationDetailsDTO
}

export interface InvoiceBulkVerificationTaskDetailsVariables {
  id: string
}

export interface ArchiveInvoiceBulkVerificationTaskData {
  archiveBulkInvoiceVerification: boolean
}

export interface ArchiveInvoiceBulkVerificationTaskVariables {
  bulkVerificationId: string
  comment: string
}

export interface ArchiveInvoiceBulkVerificationTaskFormValues {
  comment: string
}

export enum InvoiceBulkVerificationTaskFieldValues {
  COMMENT = 'comment',
}

export interface ResendBulkInvoiceVerificationData {
  resendBulkInvoiceVerification: boolean
}

export interface ResendBulkInvoiceVerificationVariables {
  emails: Array<InvoiceVerifierInput>
  bulkVerificationId: string
  verificationTemplate: InvoiceVerificationTemplate
  debtorStatement: string
}

export enum BulkInvoiceVerificationFormType {
  EXTRA_VERIFICATION = 'EXTRA_VERIFICATION',
  DEBTOR_RESPONSE = 'DEBTOR_RESPONSE',
}

export enum ApproveRejectChoice {
  APPROVE = 'APPROVE',
  REJECT = 'REJECT',
}

export interface InvoiceBulkVerificationDebtorResponse extends InvoiceDTO {
  invoiceApproval: ApproveRejectChoice
}

export interface InvoiceBulkVerificationDebtorResponseInput {
  invoiceApproval: ApproveRejectChoice
  invoiceId: string
}

export interface AddDebtorResponseForBulkInvoiceVerificationData {
  addDebtorResponseForBulkInvoiceVerification: boolean
}

export interface AddDebtorResponseForBulkInvoiceVerificationVariables {
  debtorResponseDate: string
  debtorEmailFile: DownloadableFileDTO
  comment: string
  verifierEmail: string
  approvalResponses: Array<InvoiceBulkVerificationDebtorResponseInput>
  bulkVerificationId: string
}

export enum BulkDebtorResponseFieldsNames {
  VERIFICATION_EMAIL = 'verifierEmail',
  DATE = 'debtorResponseDate',
  FILE = 'debtorEmailFile',
  COMMENT = 'comment',
  RESPONSES = 'approvalResponses',
}

export enum BulkSelectedInvoicesFieldArrayNames {
  INVOICE_APPROVAL = 'invoiceApproval',
  INVOICE_ID = 'id',
}

export interface BulkDebtorResponseFormData {
  debtorResponseDate: string
  debtorEmailFile: DownloadableFileDTO
  comment: string
  verifierEmail: string
  approvalResponses: Array<InvoiceBulkVerificationDebtorResponse>
}

export interface AddBulkInvoiceVerificationDecisionBySAData {
  addBulkInvoiceVerificationDecisionBySA: boolean
}

export interface AddBulkInvoiceVerificationDecisionBySAVariables {
  debtorEmailFile: DownloadableFileDTO
  comment: string
  approvalResponses: Array<InvoiceBulkVerificationDebtorResponseInput>
  bulkVerificationId: string
}

export interface BulkDecisionBySAFormData {
  debtorEmailFile: DownloadableFileDTO
  comment: string
  approvalResponses: Array<InvoiceBulkVerificationDebtorResponse>
}

export enum BulkDecisionBySAFieldsNames {
  FILE = 'debtorEmailFile',
  COMMENT = 'comment',
  RESPONSES = 'approvalResponses',
}

export interface InvoiceBulkVerificationCommentDTO {
  archived: boolean
  comment: string
  createdBy: string
  createdByEmail: string
  createdById: string
  createdOn: Date
  flagged: boolean
  id: string
  invoiceBulkVerificationId: string
  updatedBy: string
  updatedByEmail: string
  updatedById: string
  updatedOn: Date
}

export interface GetBulkVerificationTaskCommentsData {
  getInvoiceBulkVerificationComments: Array<InvoiceBulkVerificationCommentDTO>
}

export interface GetBulkVerificationTaskCommentsVariables {
  id: string
}

export interface GetBulkVerificationTaskCommentByIdData {
  getInvoiceBulkVerificationCommentById: InvoiceBulkVerificationCommentDTO
}

export interface GetBulkVerificationTaskCommentByIdVariables {
  commentId: string
}

export interface AddBulkVerificationTaskCommentData {
  addInvoiceBulkVerificationComment: InvoiceBulkVerificationCommentDTO
}

export interface AddBulkVerificationTaskCommentVariables {
  id: string
  comment: string
}

export interface UpdateBulkVerificationTaskCommentData {
  updateInvoiceBulkVerificationComment: InvoiceBulkVerificationCommentDTO
}

export interface UpdateBulkVerificationTaskCommentVariables {
  comment: string
  commentId: string
}

export interface MarkBulkVerificationTaskCommentAsFlaggedData {
  markInvoiceBulkVerificationCommentAsFlagged: InvoiceBulkVerificationCommentDTO
}

export interface MarkBulkVerificationTaskCommentAsFlaggedVariables {
  commentId: string
}

export interface MarkBulkVerificationTaskCommentAsUnflaggedData {
  markInvoiceBulkVerificationCommentAsUnflagged: InvoiceBulkVerificationCommentDTO
}

export interface MarkBulkVerificationTaskCommentAsUnflaggedVariables {
  commentId: string
}

export interface MarkBulkVerificationTaskCommentAsArchivedData {
  markInvoiceBulkVerificationCommentAsArchived: InvoiceBulkVerificationCommentDTO
}

export interface MarkBulkVerificationTaskCommentAsArchivedVariables {
  commentId: string
}

export interface MarkBulkVerificationTaskCommentAsUnarchivedData {
  markInvoiceBulkVerificationCommentAsUnarchived: InvoiceBulkVerificationCommentDTO
}

export interface MarkBulkVerificationTaskCommentAsUnarchivedVariables {
  commentId: string
}
